import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Chip, Tooltip, CircularProgress, Button, Dialog, IconButton, TextField, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle, Storage, Refresh, Close, Search } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import moment from 'moment';
import { DateFormats } from '../../../helpers/Constants';
import { sitePrefix } from '../../../config';
import { getChannelDescriptor } from '../../../Models/DataSources/SmartSend';
import {
    selectEmailOnIntent, selectWaActive, selectWaOnlyIntent, sourceCanSend, missingIdentity,
    selectSelectedSourceRow, channelsFrom, WaRootState,
} from '../whatsapp/selectors';
import { eDataSourceStatus } from '../../../Models/DataSources/DataSource';
import { selectSource, loadSourceColumns, selectHasMappingWork } from '../../../redux/reducers/smartSendSlice';
import { getDataSources } from '../../../redux/reducers/dataSourcesSlice';
import InlineBanner from './InlineBanner';
import { pinnedOrder } from '../pinnedOrder';

// §11.1/§13 · pick a READY, sendable source (GetMany). "Sendable" for the selected channel
// is read via the descriptor's identity-flag NAME (never a hardcoded field). READY sources
// that are NOT sendable on this channel (datalake with no identity → "view only"; a
// cell-only source on the email channel → "no email column") are shown DISABLED with a
// badge + explanation (§7.2/§16). A sendable source with 0 resolved rows is selectable but
// flagged. Source data comes from the DataSources module (getDataSources, USE_DS_MOCK).
// a11y mirrors ChannelSelector: radiogroup, roving tabindex, arrows, Enter/Space; disabled
// cards carry aria-describedby → sr-only reason and don't steal focus.

// The server clamps PageSize at 100 (DataSourcesController.cs:135), so asking for more is a lie.
// It also doubles as the marker that tells OUR response apart from the DataSources page's 6-row
// page in the shared `dataSources.list` slice — see the `isOurPage` check below.
const SOURCE_PAGE_SIZE = 100;
// QA BUG-33 — cards rendered before "show more": the same cap as CampaignPicker (CAP = 8).
const SOURCE_CAP = 8;

const useStyles = makeStyles((theme) => ({
    // QA BUG-33 r1 (Idan) — FOUR CARDS A ROW, as CampaignPicker's `list` (CampaignPicker.tsx:138-146): a
    // fixed four-column grid with the same responsive steps and gap, so the cap of 8 is two full rows. The
    // flex-wrap of fixed 264px cards it replaces fitted five on a 1920 screen (8 = 5 + 3).
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
        [theme.breakpoints.down('md')]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
        [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
        [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' },
    },
    // QA BUG-33 — the search field and the "show more" bar, copied from CampaignPicker's `toolbar` /
    // `search` / `moreBar` (CampaignPicker.tsx:104-129, :200) so the two grids look the same; copied
    // rather than imported, so neither component changes for the other. OUTLINED for the reason given
    // there (a `standard` field has no visible chrome under the app-wide underline rule).
    toolbar: {
        display: 'flex', alignItems: 'center', flexWrap: 'wrap',
        gap: theme.spacing(2), margin: `${theme.spacing(1)}px 0`,
    },
    search: {
        minWidth: 320,
        '& .MuiOutlinedInput-root': {
            background: 'var(--pls-d-surface, #fff)',
            borderRadius: 6,
            '& fieldset': { borderColor: '#a6a6a6' },
            '&:hover fieldset': { borderColor: '#797979' },
        },
        '& .MuiInputAdornment-positionStart': { color: '#797979' },
    },
    moreBar: { display: 'flex', justifyContent: 'center', marginTop: theme.spacing(2) },
    // QA BUG-33 r1 — no fixed width: the grid cell sets it (was `width: 264`). `minWidth: 0` stops a long
    // unbroken source name from widening the track (grid items default to min-content), as in CampaignPicker.
    card: {
        position: 'relative', boxSizing: 'border-box', minWidth: 0, padding: theme.spacing(2),
        border: '2px solid var(--pls-d-border, #e0e0e0)', borderRadius: 8, cursor: 'pointer', outline: 'none',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: theme.palette.primary.light },
        '&:focus, &:focus-visible': { boxShadow: `0 0 0 3px ${theme.palette.primary.light}` },
    },
    selected: { borderColor: theme.palette.primary.main, background: 'rgba(0, 0, 0, 0.02)' },
    disabled: {
        cursor: 'not-allowed', opacity: 0.6,
        '&:hover': { borderColor: 'var(--pls-d-border, #e0e0e0)' },
        '&:focus, &:focus-visible': { boxShadow: 'none' },
    },
    name: { fontWeight: 600, display: 'flex', alignItems: 'center', gap: theme.spacing(0.5), overflowWrap: 'anywhere' },
    desc: { marginTop: theme.spacing(0.5) },
    meta: { marginTop: theme.spacing(1), display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' },
    // Second metadata row: column count + last upload date. Its own row rather than more chips in
    // `meta`, so the recipients chip never wraps away from the blocked-reason chip beside it.
    sub: { marginTop: theme.spacing(0.5), display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), flexWrap: 'wrap' },
    check: { position: 'absolute', top: 8, insetInlineEnd: 8 },
    zero: { color: 'var(--pls-d-danger, #c0392b)' },
    state: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: theme.spacing(2) },
    srOnly: {
        position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
    },
    // Confirm dialog — same head/body/foot skeleton TestSendDialog uses, so the two
    // SmartSend dialogs are visually identical (MUI v4 core Dialog, no lab dependency).
    dlgHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: theme.spacing(2, 3), borderBottom: '1px solid var(--pls-d-border, #e0e0e0)' },
    dlgBody: { padding: theme.spacing(3) },
    dlgFoot: { display: 'flex', gap: theme.spacing(1.5), padding: theme.spacing(2, 3), borderTop: '1px solid var(--pls-d-border, #e0e0e0)' },
}));

interface Props {
    // SmartSend part B / invariant 24. `true` ⇒ the source cannot be changed: the e-mail of this
    // campaign has already gone out, and `CampaignsToDataSources_Set` blocks only Status = 2, so
    // a save on Status = 4 would succeed and silently rewrite what the supervisor mail and
    // "view message" resolve LIVE. Default `false` ⇒ byte-identical render.
    readOnly?: boolean;
    // 🔴 2026-09-23 — THE SOURCE IS SETTLED, SO STOP ASKING FOR IT.
    // `readOnly` is the wrong lever for this case: it renders the whole grid, disabled. When the
    // operator arrived WITH `?dataSourceId=` they answered this question one screen ago, and a
    // grid of other sources — greyed or not — reads as an unfinished step. Reported from the
    // stage test 2026-09-23: "בחר מקור appears selected, although it should be a heading".
    // `locked` renders a one-line heading naming the source, with a link that opens it in a new
    // tab. `onChangeSource` is the escape hatch back to the picker; omit it and there is none.
    locked?: boolean;
    onChangeSource?: () => void;
    // 2026-09-28 (owner, stage) — the settled source belongs BESIDE the page title, in smaller type,
    // not in a section of its own. `inline` renders the locked heading as one compact run (name ·
    // "צפייה במקור" · "החלף מקור") with no section title, and its loading / load-error states at the
    // same scale. Meaningful only with `locked`; the grid is never inline.
    inline?: boolean;
    // 12-fix-wave 7.4(d) — the send is closed (a channel of it went out): "החלף מקור" is shown disabled.
    changeDisabled?: boolean;
    // QA BUG-29 — the screen has edits that are NOT saved yet (SmartSendScreen: `dirty || waNeedsSave`).
    // Chooses the switch dialog's body only. `hasWork` (selectHasMappingWork) is true for ANY mapping on
    // screen, including the server's own saved one, so it cannot tell "unsaved" from "saved". Default
    // `false`: the picker has no unsaved edits by construction.
    unsaved?: boolean;
    // FU-06A (Idan) — the picker's wizard asks for the source FIRST, before the channels are chosen. Given, the
    // cards are gated on these channels (what the account can send) instead of the lit ones, each card says
    // which of them its columns serve («אימייל + וואטסאפ» / «אימייל בלבד» / «וואטסאפ בלבד») with its recipients
    // per channel, and the section title and the channel-mix banners are left to the wizard (its own question,
    // and its review step). Absent (the mapping page) ⇒ byte-identical render.
    wizardGate?: { email: boolean; whatsapp: boolean } | null;
}

const SourcePicker: React.FC<Props> = ({ readOnly = false, locked = false, onChangeSource, inline = false, changeDisabled = false, unsaved = false, wizardGate = null }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);
    const list = useSelector((s: any) => s.dataSources.list);
    const listStatus = useSelector((s: any) => s.dataSources.listStatus);
    const selectedChannel = useSelector((s: any) => s.smartSend.selectedChannel);
    const dataSourceId = useSelector((s: any) => s.smartSend.dataSourceId);
    const currentSourceId = useSelector((s: any) => s.smartSend.dataSource?.DataSourceID ?? s.smartSend.dataSourceId ?? null);
    const hasColumns = useSelector((s: any) => s.smartSend.columns.length > 0);
    // "Is this click going to cost the operator anything?" — the SHARED definition, so this gate
    // and the screen's autosave gate can never drift apart.
    const hasWork = useSelector(selectHasMappingWork);
    const savedMapping = useSelector((s: any) => s.smartSend.savedMapping);
    const workingIsFromServer = useSelector((s: any) => s.smartSend.workingIsFromServer);
    const campaignId = useSelector((s: any) => s.smartSend.campaignId);
    // The campaignId equality is not paranoia: clearSmartSend has zero dispatch sites, so this
    // slice survives the whole SPA session across campaigns, and a savedMapping left behind by a
    // previous campaign must never be the reason this one asks for confirmation.
    const atStake = hasWork
        || (savedMapping != null && savedMapping.campaignId === campaignId && workingIsFromServer);
    const descriptor = getChannelDescriptor(selectedChannel);
    const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

    // ── v1.23 · WHICH CHANNELS THIS CAMPAIGN ACTUALLY SENDS ON ──────────────────────────────
    // Two scalars, not one object: a selector returning a fresh object re-renders this list on
    // every dispatch in the app.
    // 🔴 C-29 — THE **INTENT**, not the state. This gate decides which source cards can be
    // picked, and the source is chosen BEFORE the leg exists — a gate that waits for the leg
    // would refuse the cell-only source at the one moment the operator needs it, which is the
    // whole point of the mode.
    const emailOn = useSelector((s: WaRootState) => selectEmailOnIntent(s));
    const waOn = useSelector((s: WaRootState) => selectWaActive(s));
    const waOnly = useSelector((s: WaRootState) => selectWaOnlyIntent(s));
    // 🔴 BUILT ONCE, READ BY ALL THREE GATES BELOW. They used to be handed `{ emailOn, waOn }`
    // separately — three private copies of "who is on", each free to drift from the others, and
    // each carrying the assumption that there are exactly two channels to name.
    const channels = channelsFrom({ email: emailOn, whatsapp: waOn });
    // FU-06A — the wizard's gate, when it gives one (two scalars, for the memo deps below).
    const wizEmail = !!wizardGate?.email;
    const wizWa = !!wizardGate?.whatsapp;
    const gateChannels = wizardGate ? channelsFrom({ email: wizEmail, whatsapp: wizWa }) : channels;
    // The LEG's own cell verdict — used for one thing only: to keep this component from
    // repeating a banner the WhatsApp tab is already showing. See the banner block below.
    const legNoCell = useSelector((s: any) => s.smartSendWa?.waMapping?.HasCellIdentity === false);
    const selectedSource = useSelector((s: WaRootState) => selectSelectedSourceRow(s));

    // 🔴 THE IDENTITY GATE — AN **OR** OVER THE ACTIVE CHANNELS (contract §6, v1.23b).
    // ⚠️ THIS LINE SAID "AND" UNTIL 16.09, AND IT WAS WRONG FROM v1.23b ONWARD. The gate
    // `selectors.ts` exports has been an OR since the owner's "וואטסאפ בלי אימייל זה כל הרעיון";
    // only this copy of the sentence stayed behind. A comment that names a stricter rule than
    // the code beside it is the C-27 shape, and it is the reason nobody re-read the line.
    //
    // It used to be `!!it[descriptor.identityFlag]`: ONE flag, chosen by `selectedChannel`.
    // That is structurally wrong the moment two channels are live, and it is wrong in a way
    // that cannot be patched inside the descriptor — `selectedChannel` cannot hold WHATSAPP at
    // all (`SmartSend.ts:32-34` marks it `enabled:false`, and `setChannel` only accepts enabled
    // channels; both files are byte-identical by contract §1). So on a WhatsApp-only campaign
    // the old gate asked about the channel that is switched OFF, and a cell-only source — the
    // exact source such a campaign exists to send from — rendered as blocked and unpickable.
    //
    // ⚠️ This gate is about which cards can be PICKED. It is NOT `waSaveState='blocked'` and it
    // is NOT `mapping.noCellBanner`, both of which come from the LEG's `HasCellIdentity` (the
    // server resolving the locked version) and neither of which ever blocks a send (§6).
    const canSend = (it: any) => sourceCanSend(it, gateChannels);

    // Fetch exactly once per mount, tracked by a ref rather than inferred from store state.
    // Two reasons this is not `!list && listStatus === '...'`:
    //  1. Any store-derived condition re-satisfies itself after a failure (list stays null), which
    //     looped the endpoint forever and made the retry button below unreachable.
    //  2. `list` may already hold a SEARCHED or small-page result left by the DataSources page,
    //     and gating on `!list` would silently show that filtered subset here instead of the
    //     full PageSize:100 list this screen needs.
    // The retry button dispatches directly, so it still works with the ref already set.
    // PageSize is 100, not the 200 this used to ask for: DataSourcesController.cs:135 clamps with
    // Math.Min(pageSize <= 0 ? 6 : pageSize, 100), so 200 was never honoured. Asking for the real
    // ceiling keeps the request honest and makes list.pageSize/list.total mean what they say —
    // the caption below is what tells the user when there is more than one page of sources.
    const didFetch = useRef(false);
    useEffect(() => {
        if (didFetch.current) return;
        didFetch.current = true;
        dispatch(getDataSources({ PageIndex: 1, PageSize: SOURCE_PAGE_SIZE, SearchTerm: '' }));
    }, [dispatch]);

    const ready = useMemo(
        () => ((list && list.items) ? list.items : []).filter((it: any) => it.Status === eDataSourceStatus.READY),
        [list],
    );
    // QA BUG-33 (Idan) — THE GRID IS SEARCHABLE AND CAPPED, as CampaignPicker and the WhatsApp templates
    // already are (CampaignPicker.tsx:256-337): an account with 16+ sources was one long wall of cards.
    // Client-side over the list already fetched (up to 100); the fetch, `ready`, the preselect, the
    // incoming check and `pick` all keep reading the FULL `ready`. Only what is RENDERED is cut: `capped`
    // shows SOURCE_CAP cards until "show more" (QA BUG-34: the pre-selected source first, see below). Every
    // index-based helper below (sendableIdx, rovingTarget, cardRefs) is over `capped`, the rendered list.
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(false);
    const matches = useMemo(() => {
        const term = search.trim().toLowerCase();
        return term ? ready.filter((it: any) => String(it.Name ?? '').toLowerCase().indexOf(term) > -1) : ready;
    }, [ready, search]);
    // QA BUG-34 (Idan) — the source selected when OUR list first arrived (on the mapping page's grid, the
    // row's source; on the picker, nothing after `resetForPicker`) is shown FIRST and the grid stays capped
    // (`pinnedOrder`), replacing "the selection sorts past the cap ⇒ render every match". Taken ONCE per
    // mount, never from a later pick, so a card picked after «הצג עוד» does not jump. A ref written once
    // during render (idempotent).
    const pinRef = useRef<number | null | undefined>(undefined);
    if (pinRef.current === undefined && ready.length > 0 && listStatus === 'succeeded'
        && (list?.pageSize ?? 0) >= SOURCE_PAGE_SIZE) {
        pinRef.current = currentSourceId ?? null;
    }
    const pinnedId = pinRef.current ?? null;
    const capped = useMemo(() => {
        const ordered = pinnedOrder(matches, (it: any) => it.DataSourceID === pinnedId, (it: any) => canSend(it), SOURCE_CAP);
        return expanded ? ordered : ordered.slice(0, SOURCE_CAP);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matches, expanded, pinnedId, emailOn, waOn, wizEmail, wizWa]);
    const moreCount = matches.length - capped.length;   // cards the cap is still holding back
    const sendableIdx = useMemo(
        () => capped.reduce((acc: number[], it: any, i: number) => (canSend(it) ? [...acc, i] : acc), []),
        // The gate's inputs are now the CHANNEL FLAGS, not the descriptor's field name — leaving
        // `descriptor.identityFlag` here would have frozen this list at whatever the flags were
        // when it was first computed, since that string never changes any more.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [capped, emailOn, waOn, wizEmail, wizWa],
    );

    // Preselect (entry A ?dataSourceId): load the source's columns once, if it is sendable.
    // NaN-guarded — a garbage ?dataSourceId never dispatches (R2 note).
    // The latch records WHICH id we already loaded, and is set only when we actually dispatch.
    // Two failure modes it has to avoid at once:
    //  - latching on "the list arrived" loses the preselect entirely when that first list is a
    //    stale/filtered one left by the DataSources page that does not contain the target: the
    //    fresh PageSize:100 list then arrives to an already-burnt latch and nothing ever loads.
    //    Not finding the target is therefore NOT a latch — we simply wait for the next list.
    //  - not latching at all re-fires on a manual pick, because `pick` sets dataSourceId and
    //    clears columns via selectSource, flipping both deps; `pick` claims the id itself.
    // `idNum <= 0` also rejects `?dataSourceId=` (empty), which Number() turns into 0.
    const preselectedFor = useRef<number | null>(null);
    useEffect(() => {
        const idNum = Number(dataSourceId);
        if (!list || dataSourceId == null || Number.isNaN(idNum) || idNum <= 0 || hasColumns) return;
        if (preselectedFor.current === idNum) return;
        const pre = ready.find((it: any) => it.DataSourceID === idNum && canSend(it));
        if (!pre) return;
        preselectedFor.current = idNum;
        dispatch(loadSourceColumns(idNum));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list, dataSourceId, hasColumns]);

    // Entry A can arrive with a ?dataSourceId that is NOT the source this campaign is already
    // mapped to. PRODUCT DECISION: the SAVED MAPPING WINS and is never overwritten automatically
    // — that is exactly what the `|| hasColumns` bail in the effect above does, since getMapping
    // has already filled columns for the mapped source. But losing silently is the bug: the
    // screen would simply ignore the source the user pressed "Smart Send" from, with no clue why.
    // So make the conflict visible and OFFER the switch; never take it.
    // All three conditions must hold:
    //  • a positive incoming id is stored (SmartSendScreen already rejects <= 0 / NaN; re-checked
    //    here because this component reads the store, not the URL),
    //  • it resolves to a READY, sendable-on-this-channel source in this list — an unknown,
    //    deleted or view-only id gets NO banner, because there is nothing we could switch to,
    //  • the campaign is genuinely mapped to a DIFFERENT source. `dataSource` is null both for an
    //    unmapped campaign (nothing to protect — the preselect above just loads the incoming one)
    //    and for the whole window between selectSource and loadSourceColumns.fulfilled, so
    //    requiring a non-null mapped id is also what stops the banner flashing back for a frame
    //    right after the user accepts the switch.
    const mappedSourceId = useSelector((s: any) => s.smartSend.dataSource?.DataSourceID ?? null);
    const incoming = useMemo(() => {
        const idNum = Number(dataSourceId);
        if (dataSourceId == null || Number.isNaN(idNum) || idNum <= 0) return null;
        if (mappedSourceId == null || mappedSourceId === idNum) return null;
        return ready.find((it: any) => it.DataSourceID === idNum && canSend(it)) ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSourceId, mappedSourceId, ready, emailOn, waOn, wizEmail, wizWa]);

    // Switching source CLEARS THE SCREEN: selectSource (smartSendSlice.ts:419-439) drops tokenMap,
    // supervisorColumnId, gapColumnId, sortColumnId, lockedVersionId, columns, sampleValues and
    // isMapped — up to 20 hand-made field mappings. It writes NOTHING to the server, and
    // `savedMapping` deliberately survives it, so a mapping that was already SAVED is recoverable
    // from the screen (the restore banner in SmartSendScreen) right up until the next successful
    // setMapping under the new source. UNSAVED edits are not recoverable at all.
    //
    // Hence the confirm below, and hence it is now reachable from EVERY route into a switch — the
    // card click, the keyboard (Enter/Space on the radiogroup), and the incoming-source banner —
    // instead of the banner alone. `pendingPick` HOLDS THE CANDIDATE and dispatches nothing:
    // cancelling is the absence of an event, not an undo.
    //
    // A BOOLEAN HERE WAS A TRAP. The dialog used to live inside the `{incoming && (` block, so a
    // `confirmOpen` flag flipped from a card click rendered NO dialog on the common path (incoming
    // is null unless a ?dataSourceId= names a different source) — source switching would have
    // stopped working silently. Holding the candidate itself, in a dialog rendered unconditionally,
    // makes that unrepresentable. No auto-focus / ok-by-default shortcut: the destructive button is
    // a deliberate second click.
    const [pendingPick, setPendingPick] = useState<any | null>(null);

    // The old placement gave the dialog structural self-invalidation (it unmounted with `incoming`).
    // Hoisted out, that guarantee becomes a convention and has to be written down: never act on a
    // candidate chosen before the current source changed underneath us.
    useEffect(() => { setPendingPick(null); }, [currentSourceId]);

    const doPick = (it: any) => {
        // Claim the id before dispatching so the preselect effect above — which is about to
        // re-run, since selectSource changes dataSourceId and clears columns — sees it as
        // already handled and does not fire a duplicate load.
        preselectedFor.current = it.DataSourceID;
        dispatch(selectSource(it.DataSourceID));
        dispatch(loadSourceColumns(it.DataSourceID));
    };

    const pick = (it: any) => {
        // THE choke point: `doPick` is reached only through here (the card click, the keyboard
        // handler and the "switch" button all call it), so one guard covers every path.
        if (readOnly) return;
        if (!canSend(it) || it.DataSourceID === currentSourceId) return;
        // NO FRICTION WHEN NOTHING IS AT STAKE. Asking on a campaign with nothing to lose is its
        // own kind of unprofessional, and it would fire on the FIRST source pick of every new
        // campaign. `atStake` is true either because there is unsaved work in the table, or
        // because what is on screen is the server's own mapping and this click is the first
        // departure from it. Browsing on between candidate sources afterwards costs nothing more:
        // the server row is still intact and the restore banner is still on screen.
        if (!atStake) { doPick(it); return; }
        setPendingPick(it);
    };

    const moveFocus = (fromIdx: number, dir: 1 | -1) => {
        if (!sendableIdx.length) return;
        const pos = sendableIdx.indexOf(fromIdx);
        const next = sendableIdx[(pos + dir + sendableIdx.length) % sendableIdx.length];
        cardRefs.current[next]?.focus();
    };
    const onKeyDown = (e: React.KeyboardEvent, idx: number, it: any) => {
        if (!canSend(it)) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(it); }
        else if (e.key === 'ArrowDown' || e.key === 'ArrowLeft') { e.preventDefault(); moveFocus(idx, 1); }
        else if (e.key === 'ArrowUp' || e.key === 'ArrowRight') { e.preventDefault(); moveFocus(idx, -1); }
    };

    // Spin ONLY while the fetch is genuinely outstanding ('idle' before the post-paint dispatch,
    // then 'loading') so the empty "no sources" message never flashes before the first fetch
    // (§ה loading-state mandate). Anything else with no list is terminal and falls through to the
    // retry block below — including StatusCode!=200 responses, which this API returns inside an
    // HTTP 200 envelope, so the thunk resolves as fulfilled and listStatus becomes 'succeeded'
    // with list still null. Keying the spinner on `!== 'failed'` would hang it there forever.
    // `!didFetch.current` covers the very first render: the fetch is dispatched in a post-paint
    // effect, so a store left in a terminal state by another screen would otherwise flash the
    // error block for one frame before our own request starts.
    const compact = inline && locked;
    if (!list && (!didFetch.current || listStatus === 'idle' || listStatus === 'loading')) {
        if (compact) return <CircularProgress size={14} />;
        return (
            <Box style={wizardGate ? undefined : { marginTop: 24 }}>
                {!wizardGate && <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{t('DataSources.send.source.title')}</Typography>}
                <Box className={classes.state}><CircularProgress size={20} /></Box>
            </Box>
        );
    }
    // Terminal with no list: a rejected request ('failed') OR a resolved-but-empty envelope
    // ('succeeded' with Data null — the dominant server-error path here). Both get the retry.
    if (!list) {
        const retry = () => dispatch(getDataSources({ PageIndex: 1, PageSize: SOURCE_PAGE_SIZE, SearchTerm: '' }));
        if (compact) {
            return (
                <Box style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="error" component="span">{t('DataSources.send.source.loadError')}</Typography>
                    <Button size="small" startIcon={<Refresh />} onClick={retry}>{t('DataSources.retry')}</Button>
                </Box>
            );
        }
        return (
            <Box style={wizardGate ? undefined : { marginTop: 24 }}>
                {!wizardGate && <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{t('DataSources.send.source.title')}</Typography>}
                <Box className={classes.state}>
                    <Typography color="error">{t('DataSources.send.source.loadError')}</Typography>
                    <Button size="small" startIcon={<Refresh />} onClick={() => dispatch(getDataSources({ PageIndex: 1, PageSize: SOURCE_PAGE_SIZE, SearchTerm: '' }))}>
                        {t('DataSources.retry')}
                    </Button>
                </Box>
            </Box>
        );
    }

    // Roving tabindex: exactly one sendable card is tabbable (the selected one, else the first).
    const selectedIdx = capped.findIndex((it: any) => canSend(it) && it.DataSourceID === currentSourceId);
    const rovingTarget = selectedIdx >= 0 ? selectedIdx : (sendableIdx.length ? sendableIdx[0] : -1);

    // The server clamps PageSize to 100 (DataSourcesController.cs:135) and echoes back the TRUE
    // total, so an account with more than 100 sources is served a truncated list. This screen has
    // no paging and no search (both out of scope), so sources 101+ are unreachable here — the one
    // thing we must not do is let them vanish silently. Counted off `list.items`, the raw fetched
    // page, not `ready`: this caption is about what the server withheld, not about what the READY
    // /sendable filters removed from what it sent.
    // `list` is shared with the DataSources page, which fetches 6 rows at a time and can leave a
    // searched/small page in the store. Until OUR request lands, `total` and `items` are that
    // screen's numbers and would read as truncation on an account that has none. The echoed
    // pageSize identifies whose response this is: that screen asks for 6, this one for 100.
    const fetched = ((list && list.items) ? list.items : []).length;
    const isOurPage = (list.pageSize ?? 0) >= SOURCE_PAGE_SIZE;
    const truncated = isOurPage && list.total > fetched;

    // 🔴 v1.23 · THE BIDIRECTIONAL WARNING — A WARNING, NOT A BLOCK (§6/§7).
    // The gate above decides which cards can be PICKED. This describes the source that is
    // ALREADY picked, and the two are not the same question: a campaign mapped to an e-mail-only
    // source before it was given a WhatsApp leg (or the reverse) arrives here with a selection
    // the gate would refuse today. Un-picking it silently, or disabling Send, would both be
    // worse than saying so — §6 is explicit that a missing identity column never blocks.
    // The SHARED definition — the removal dialog reads the same row for its "can this campaign
    // switch to e-mail" question, and two private copies of "which source is this" drift.
    const selectedMissing = selectedSource
        ? missingIdentity(selectedSource, channels)
        : 'none';
    // ⚠️ `waOn &&` — these two banners are the v1.23 bidirectional warning, and v1.23 only
    // exists where the add-on does. With WhatsApp off, `?dataSourceId=` can still point this
    // screen at a source with no e-mail column (the URL is validated as a positive number, not
    // for sendability), and rendering a banner there would put a new element on a screen the
    // contract requires to stay pixel-identical when the add-on is off (14-W3 §2).
    // 🔴 v1.23b · FOUR KEYS, NOT TWO — and the split is about one clause. Each banner's
    // statement about ITS OWN channel is always true ("הווטסאפ לא יישלח"); the reassurance
    // about the OTHER one ("המייל יישלח כרגיל") is true only while that other channel is lit.
    // `noCellBanner` carried the reassuring wording unconditionally until now, so a
    // WhatsApp-only campaign was promised a mail that cannot go out — on screen, in the running
    // build. The `*Both` pair is where that clause lives now.
    const bothOn = emailOn && waOn;
    const noEmailKey = bothOn
        ? 'DataSources.send.whatsapp.mapping.noEmailBannerBoth'
        : 'DataSources.send.whatsapp.mapping.noEmailBanner';
    const noCellKey = bothOn
        ? 'DataSources.send.whatsapp.mapping.noCellBannerBoth'
        : 'DataSources.send.whatsapp.mapping.noCellBanner';
    // FU-06A — not in the wizard: the channel mix is chosen AFTER this step there, and its review step shows these.
    const warnNoEmail = !wizardGate && waOn && (selectedMissing === 'email' || selectedMissing === 'both');
    // ⚠️ `&& !legNoCell` — the WhatsApp tab renders `mapping.noCellBanner` from the LEG's own
    // verdict, and in WhatsApp-only mode there are no tabs, so both surfaces are on screen at
    // once. One fact, one banner: when the leg has already said it, this stays quiet.
    const warnNoCell = !wizardGate && waOn && (selectedMissing === 'cell' || selectedMissing === 'both') && !legNoCell;

    // 🔴 LOCKED — the settled-source heading. Placed AFTER the loading and load-error returns
    // above, deliberately: the name is resolved from the same list those returns are waiting
    // for, so locking before it lands would render a heading with no name. It adds no hook (all
    // hooks live above every early return in this file), so hook order is unaffected.
    // The identity warnings below are NOT repeated here: they belong to the act of choosing.
    if (locked) {
        const name: string | null = selectedSource?.Name ?? null;
        // QA BUG-06 (C-06) — «החלף מקור» is disabled only for a closed send (`changeDisabled`, D1), and a
        // disabled button fires no mouse events, so the D1 reason hovers on a wrapping span (the grid's
        // idiom below). Live, the button renders bare, as before.
        const changeTip = (button: React.ReactElement) => (changeDisabled
            ? (
                <Tooltip title={t('DataSources.send.errors.sendClosed')} PopperProps={{ dir: isRTL ? 'rtl' : 'ltr' }}>
                    <span style={{ display: 'inline-flex' }}>{button}</span>
                </Tooltip>
            )
            : button);
        if (compact) {
            return (
                <Box style={{ display: 'inline-flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                    {/* An id is meaningless to the operator, so an unresolved name renders the
                        generic word rather than a number (as below). */}
                    <Typography variant="body2" component="span" style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {name ?? t('DataSources.send.source.title')}
                    </Typography>
                    {currentSourceId != null && (
                        <Typography variant="body2" component="span">
                            <a
                                href={`${sitePrefix}DataSources/View/${currentSourceId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {t('DataSources.send.source.viewLink')}
                            </a>
                        </Typography>
                    )}
                    {onChangeSource && changeTip(
                        <Button size="small" disabled={changeDisabled} onClick={onChangeSource} style={{ padding: '0 6px', minWidth: 0 }}>
                            {t('DataSources.send.source.change')}
                        </Button>
                    )}
                </Box>
            );
        }
        return (
            <Box style={{ marginTop: 24 }}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{t('DataSources.send.source.title')}</Typography>
                <Box style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {/* An id is meaningless to the operator, so an unresolved name renders the
                        generic word rather than a number — the same judgement SmartSendPicker's
                        source chip makes (SmartSendPicker.tsx:165-175). */}
                    <Typography style={{ fontWeight: 600 }}>
                        {name ?? t('DataSources.send.source.title')}
                    </Typography>
                    {currentSourceId != null && (
                        <a
                            href={`${sitePrefix}DataSources/View/${currentSourceId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('DataSources.send.source.viewLink')}
                        </a>
                    )}
                    {onChangeSource && changeTip(
                        <Button size="small" disabled={changeDisabled} onClick={onChangeSource}>
                            {t('DataSources.send.source.change')}
                        </Button>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box style={wizardGate ? undefined : { marginTop: 24 }}>
            {!wizardGate && <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{t('DataSources.send.source.title')}</Typography>}
            {(warnNoEmail || warnNoCell) && (
                <Box style={{ marginTop: 8 }}>
                    {warnNoEmail && (
                        <InlineBanner
                            severity="warning"
                            role="status"
                            title={t(noEmailKey)}
                        />
                    )}
                    {warnNoCell && (
                        <InlineBanner
                            severity="warning"
                            role="status"
                            title={t(noCellKey)}
                        />
                    )}
                </Box>
            )}
            {incoming && (
                <Box style={{ marginTop: 8 }}>
                    <InlineBanner
                        severity="info"
                        role="status"
                        title={t('DataSources.send.source.incomingTitle')}
                        body={t('DataSources.send.source.incomingBody', { name: incoming.Name })}
                        action={(
                            /* Proposes the candidate — it does NOT switch. `pick` runs the same
                               at-stake gate a card click does, so this button lands on the same
                               confirm (or, on an unmapped campaign, switches straight away). */
                            <Button size="small" variant="outlined" color="primary" onClick={() => pick(incoming)}>
                                {t('DataSources.send.source.incomingSwitch')}
                            </Button>
                        )}
                    />
                </Box>
            )}
            {/* HOISTED OUT of the `{incoming && (` block. It used to live inside it, which meant a
                card click could never reach it — see the `pendingPick` comment above for why that
                made a boolean flag unsafe here. It now renders unconditionally and is driven purely
                by whether a candidate is held. `pendingPick` is nulled whenever currentSourceId
                changes, which replaces the self-invalidation the old placement gave for free. */}
            {pendingPick && (
                    <Dialog
                        open
                        onClose={() => setPendingPick(null)}
                        maxWidth="sm"
                        fullWidth
                        // Portalled to document.body, outside App.js:1018's <div dir=...>, and
                        // <html dir> is stuck at "ltr" (App.js:727-730 has an empty dep array and
                        // runs before i18n.changeLanguage at App.js:806). Without this the dialog
                        // renders LTR for Hebrew users.
                        dir={isRTL ? 'rtl' : 'ltr'}
                        aria-labelledby="smartsend-switch-confirm-title"
                        aria-describedby="smartsend-switch-confirm-body"
                    >
                        <Box className={classes.dlgHead}>
                            <Typography variant="h6" id="smartsend-switch-confirm-title">
                                {t('DataSources.send.source.switchConfirmTitle')}
                            </Typography>
                            <IconButton size="small" onClick={() => setPendingPick(null)} aria-label={t('DataSources.send.close')}>
                                <Close />
                            </IconButton>
                        </Box>
                        <Box className={classes.dlgBody}>
                            {/* Two bodies, because the two situations have OPPOSITE outcomes and
                                saying so is the whole point. Unsaved work is genuinely gone;
                                a saved mapping is not deleted until the next save under the new
                                source, and can be restored in one click until then. Telling the
                                operator "this cannot be undone" when it can is what teaches them
                                to distrust the screen. Unsaved work first: if there is ANY, the
                                destructive statement is the true one.
                                QA BUG-29 — `unsaved` (the screen's own flag), NOT `hasWork`: a saved,
                                untouched mapping made `hasWork` true and showed the Unsaved body over
                                nothing. With no saved mapping of THIS campaign to name, the Saved body
                                has nothing true to say, so the Unsaved one stays. */}
                            <Typography variant="body2" id="smartsend-switch-confirm-body">
                                {(unsaved || !(savedMapping != null && savedMapping.campaignId === campaignId))
                                    ? t('DataSources.send.source.switchConfirmBodyUnsaved', { to: pendingPick.Name })
                                    : t('DataSources.send.source.switchConfirmBodySaved', {
                                        to: pendingPick.Name,
                                        from: savedMapping?.dataSourceName ?? '',
                                        mapped: (savedMapping?.mappedTokenCount ?? 0).toLocaleString(),
                                        total: (savedMapping?.tokenCount ?? 0).toLocaleString(),
                                    })}
                            </Typography>
                        </Box>
                        <Box className={classes.dlgFoot}>
                            {/* The ONLY route from a held candidate into an actual dispatch. It runs
                                the exact same selectSource + loadSourceColumns a manual card click
                                does — including claiming the preselect latch, which stops the effect
                                above from firing a duplicate load when dataSourceId/columns flip.
                                Clear the candidate FIRST so the dialog never lingers over the
                                re-rendering picker, and read it into a local so the handler cannot
                                act on a value the clear has already dropped. */}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => { const it = pendingPick; setPendingPick(null); if (it) doPick(it); }}
                            >
                                {t('DataSources.send.source.switchConfirmAction')}
                            </Button>
                            <Button onClick={() => setPendingPick(null)}>{t('DataSources.send.cancel')}</Button>
                        </Box>
                    </Dialog>
            )}
            {/* QA BUG-33 — the search, only when the list is longer than one capped screen: a short list
                is fully visible, and a search box over three cards is one more control for nothing.
                `placeholder`, not `label`, and an `aria-label`, as CampaignPicker does. */}
            {ready.length > SOURCE_CAP && (
                <Box className={classes.toolbar}>
                    <TextField
                        variant="outlined"
                        placeholder={t('DataSources.send.source.searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        inputProps={{ 'aria-label': t('DataSources.send.source.searchPlaceholder') }}
                        size="small"
                        className={classes.search}
                    />
                </Box>
            )}
            {!ready.length ? (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    {t('DataSources.send.source.empty')}
                </Typography>
            ) : !matches.length ? (
                <Box style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="textSecondary">{t('DataSources.send.source.noResults')}</Typography>
                    <Button size="small" color="primary" onClick={() => setSearch('')}>{t('DataSources.send.picker.clearSearch')}</Button>
                </Box>
            ) : (
                <>
                <Box className={classes.grid} role="radiogroup" aria-label={t('DataSources.send.source.title')}>
                    {capped.map((it: any, idx: number) => {
                        const sendable = canSend(it);
                        // `sendable &&` matters: a stale/hand-typed ?dataSourceId pointing at a
                        // view-only or wrong-channel source would otherwise paint that card as
                        // checked (border + CheckCircle + aria-checked) while `pick` refuses it —
                        // a checked radio the user cannot uncheck, on a screen that then shows
                        // neither columns nor an error.
                        const isSelected = sendable && it.DataSourceID === currentSourceId;
                        // 🔴 THE COUNT FOLLOWS THE CHANNEL THAT IS ACTUALLY SENDING.
                        // `descriptor.resolvedCountField` is `ResolvedRowsEmail` for as long as
                        // `selectedChannel` is EMAIL — which is always — so on a WhatsApp-only
                        // campaign every card advertised the number of E-MAIL recipients for a
                        // send that will not include a single one of them.
                        const count = (waOnly ? it.ResolvedRowsCell : it[descriptor.resolvedCountField]) ?? 0;
                        const zeroResolved = sendable && count === 0;
                        // 🔴 WHICH identity is missing, given the channels that are ON — the same
                        // AND that decides `sendable`, read the other way round so the chip names
                        // the real shortfall instead of always saying "no e-mail column".
                        //
                        // 🔴 v1.23e · C-17 — THREE REASONS, AND THE ORDER IS MANDATORY AND
                        // EXHAUSTIVE. Written out here as the contract states it, because the
                        // helper's three outcomes are the same partition read the other way:
                        //    no identity at all        → source.viewOnly
                        //    waOn && !HasCellIdentity  → source.blockedNoCell
                        //    otherwise                 → source.blockedNoEmail
                        //
                        // ⚠️ WHAT IT REPLACES SAID THE OPPOSITE OF ITSELF. The original
                        // `it.HasCellIdentity ? 'blockedNoEmail' : 'viewOnly'` explained a card
                        // blocked on a WhatsApp-only campaign — a source with an e-mail column
                        // and no phone number — as "אין עמודת אימייל", on a source that has one.
                        //
                        // ⚠️ And `blockedNoCell` is the FIRST key this feature adds under
                        // `send.source.*` rather than `send.whatsapp.*`. That is deliberate and
                        // correct: it is a sibling of `blockedNoEmail` and `viewOnly`, carries
                        // their sentence shape ("<what is missing> — <what cannot be done>"), and
                        // belongs to the picker, not to the WhatsApp subtree. It therefore sits
                        // OUTSIDE the §7 parity group — see the dedicated three-language check in
                        // `i18nParity.selftest.test.ts`.
                        const missing = missingIdentity(it, gateChannels);
                        const blockedKey = missing === 'both'
                            ? 'viewOnly'
                            : (missing === 'cell' ? 'blockedNoCell' : 'blockedNoEmail');
                        const blockedText = t(`DataSources.send.source.${blockedKey}`);
                        // 0 also covers "the SP script is not deployed yet" — see the JSX comment.
                        const fields = Number(it.ColumnsCount) > 0 ? Number(it.ColumnsCount) : 0;
                        const when = it.LastUploadDate ? moment(it.LastUploadDate) : null;
                        const uploaded = when && when.isValid() ? when.format(DateFormats.DATE_TIME_24) : null;
                        const descId = `smartsend-src-desc-${it.DataSourceID}`;
                        // `aria-label` below sets the card's accessible NAME, which suppresses all of
                        // its inner text for screen readers — so the recipients chip, the blocked
                        // reason and the zero-resolved caption were already unannounced before this
                        // change. Rather than fold the two new facts into the name (which would
                        // announce columns+date but still not the recipient count — the worst of both),
                        // everything the card shows is collected into ONE sr-only description node,
                        // now rendered for every card and not only for blocked ones.
                        // `source.zeroResolved` says "אין שורות שנפתרו לאימייל במקור זה" — an
                        // e-mail sentence. On a WhatsApp-only campaign the count above is the
                        // CELL count, so the §7 key for exactly that fact is used instead rather
                        // than printing a true number under a false explanation.
                        const zeroText = waOnly
                            ? t('DataSources.send.whatsapp.error.WA_NO_RECIPIENTS')
                            : t('DataSources.send.source.zeroResolved');
                        // FU-06A — in the wizard (with WhatsApp in the account): which channels this source's
                        // columns serve, and its recipients on each. The channel is chosen in the next step,
                        // so no count stands for "the" channel here.
                        const serves = wizardGate && wizWa && sendable
                            ? { email: wizEmail && !!it.HasEmailIdentity, wa: !!it.HasCellIdentity }
                            : null;
                        const tagKey = serves && (serves.email && serves.wa ? 'tagBoth' : (serves.wa ? 'tagWa' : 'tagEmail'));
                        const perChannel = serves ? [
                            serves.email ? { key: 'email', n: Number(it[descriptor.resolvedCountField] ?? 0), text: 'countEmail' } : null,
                            serves.wa ? { key: 'wa', n: Number(it.ResolvedRowsCell ?? 0), text: 'countWa' } : null,
                        ].filter(Boolean) as { key: string; n: number; text: string }[] : [];
                        const perChannelText = perChannel
                            .map((c) => t(`DataSources.send.picker.wizard.${c.text}`, { count: c.n.toLocaleString() })).join(' · ');
                        const description = [
                            !sendable ? blockedText : (serves
                                ? `${t(`DataSources.send.picker.wizard.${tagKey}`)}. ${perChannelText}`
                                : t('DataSources.send.source.recipients', { count: count.toLocaleString() })),
                            zeroResolved && !serves ? zeroText : null,
                            fields > 0 ? t('DataSources.send.source.columns', { n: fields.toLocaleString() }) : null,
                            uploaded ? t('DataSources.send.source.updated', { date: uploaded }) : null,
                        ].filter(Boolean).join('. ');

                        const card = (
                            <div
                                ref={(el) => { cardRefs.current[idx] = el; }}
                                className={clsx(classes.card, isSelected && classes.selected, (!sendable || readOnly) && classes.disabled)}
                                role="radio"
                                aria-checked={isSelected}
                                aria-disabled={(sendable && !readOnly) ? undefined : true}
                                aria-label={it.Name}
                                aria-describedby={descId}
                                tabIndex={sendable ? (idx === rovingTarget ? 0 : -1) : -1}
                                onMouseDown={sendable ? undefined : (e) => e.preventDefault()}
                                onClick={() => pick(it)}
                                onKeyDown={(e) => onKeyDown(e, idx, it)}
                            >
                                {isSelected && <CheckCircle className={classes.check} color="primary" fontSize="small" />}
                                <Typography className={classes.name} title={it.Name}>
                                    <Storage fontSize="small" />{it.Name}
                                </Typography>
                                {it.Description && (
                                    <Typography variant="body2" color="textSecondary" className={classes.desc}>{it.Description}</Typography>
                                )}
                                {serves ? (
                                    <Box className={classes.meta}>
                                        <Chip size="small" color="primary" variant="outlined" label={t(`DataSources.send.picker.wizard.${tagKey}`)} />
                                        {perChannel.map((c) => (
                                            <Typography key={c.key} variant="caption" className={c.n === 0 ? classes.zero : undefined} color={c.n === 0 ? undefined : 'textSecondary'}>
                                                {t(`DataSources.send.picker.wizard.${c.text}`, { count: c.n.toLocaleString() })}
                                            </Typography>
                                        ))}
                                    </Box>
                                ) : (
                                    <Box className={classes.meta}>
                                        {sendable
                                            ? <Chip size="small" label={t('DataSources.send.source.recipients', { count: count.toLocaleString() })} />
                                            : <Chip size="small" label={blockedText} />}
                                        {zeroResolved && (
                                            <Typography variant="caption" className={classes.zero}>{zeroText}</Typography>
                                        )}
                                    </Box>
                                )}
                                {/* How many columns the file has, and when it was last uploaded.
                                    ColumnsCount comes from DataSourceVersions.ColumnCount, projected
                                    by DataSources_GetMany; it is 0 until that SP script is deployed
                                    (GInt returns 0 for an absent column), so render nothing rather
                                    than a false "0 columns".
                                    LastUploadDate is the LATEST VERSION's CreatedDate — re-uploading
                                    the same source creates a new version and leaves DataSources.
                                    CreatedDate untouched, so this is the only field that means "last
                                    updated". Plain moment(), not CampaignPicker's parseServerDate:
                                    that helper exists for the campaigns endpoint's '.FFF' masks,
                                    whereas this is a Newtonsoft-serialised DateTime? (ISO-8601).
                                    Mirrors DataSources.tsx and SmartSendManageTab, which both use
                                    raw moment on this same payload. */}
                                {(fields > 0 || uploaded) && (
                                    <Box className={classes.sub}>
                                        {fields > 0 && (
                                            <Typography variant="caption" color="textSecondary">
                                                {t('DataSources.send.source.columns', { n: fields.toLocaleString() })}
                                            </Typography>
                                        )}
                                        {uploaded && (
                                            <Typography variant="caption" color="textSecondary">
                                                {t('DataSources.send.source.updated', { date: uploaded })}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                <span id={descId} className={classes.srOnly}>{description}</span>
                            </div>
                        );

                        // QA BUG-33 r1 — a blocked card's span is the grid item now: block + full width so
                        // it fills its cell like the others (CampaignPicker `tooltipSpan`).
                        return sendable
                            ? <React.Fragment key={it.DataSourceID}>{card}</React.Fragment>
                            : (
                                <Tooltip key={it.DataSourceID} title={blockedText}>
                                    <span style={{ display: 'block', width: '100%' }}>{card}</span>
                                </Tooltip>
                            );
                    })}
                </Box>
                {/* QA BUG-33 — the cap expander, CampaignPicker's: centred, neutral, outlined. */}
                {moreCount > 0 && (
                    <Box className={classes.moreBar}>
                        <Button size="small" variant="outlined" onClick={() => setExpanded(true)}>
                            {t('DataSources.send.picker.showMore', { count: moreCount.toLocaleString() })}
                        </Button>
                    </Box>
                )}
                </>
            )}
            {/* Rendered outside the ready/empty branches on purpose: "no sources are ready to send"
                is a different and much more confusing message when the server only sent us the
                first 100 of many. */}
            {truncated && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    {t('DataSources.send.source.truncated', {
                        shown: fetched.toLocaleString(), total: (list.total ?? 0).toLocaleString(),
                    })}
                </Typography>
            )}
        </Box>
    );
};

export default SourcePicker;
